import { AddItemConfig } from '../classes/add-item/add-item-config';
import { ArrayComparisonConfig } from '../classes/array-comparison-task/array-comparison-config';
import { ArrayToRecordsConfig } from '../classes/array-to-records-task/array-to-records-config';
import { BatchingConfig } from '../classes/batching-task/batching-config';
import { ClearDataConfig } from '../classes/clear-data-task/clear-data-config';
import { CustomLogConfig } from '../classes/custom-log-task/custom-log-config';
import { DataMapperConfig } from '../classes/data-mapper/data-mapper-config';
import { DBConnectorConfig } from '../classes/db-connector/db-connector-config';
import { DelayConfig } from '../classes/delay/delay.config';
import { DuplicateValueCheckConfig } from '../classes/duplicate-value-check/duplicate-value-check-config';
import { ExecuteFlowConfig } from '../classes/execute-flow/execute-flow-config';
import { GoogleSheetsQueryConfig } from '../classes/google/google-sheets-query';
import { HubSpotWebhookConfig } from '../classes/hubspot-webhook/hubspot-webhook-config';
import { HubSpotDeleteObjectsConfig } from '../classes/hubspot/hubspot-delete-objects-config';
import { HubSpotGetAssociationsConfig } from '../classes/hubspot/hubspot-get-associations-config';
import { HubSpotGetObjectsConfig } from '../classes/hubspot/hubspot-get-objects-config';
import { HubSpotSaveAssociationConfig } from '../classes/hubspot/hubspot-save-association-config';
import { HubSpotSaveObjectsConfig } from '../classes/hubspot/hubspot-save-objects-config';
import { IfGateConfig } from '../classes/if-gate/if-gate-config';
import { LoopConfig } from '../classes/loop-task/loop-config';
import { MergeDataConfig } from '../classes/merge-data/merge-data-config';
import { NotificationConfig } from '../classes/notification/notification-config';
import { ParallelJoinConfig } from '../classes/parallel-gateways/parallel-join-config';
import { ParallelSplitConfig } from '../classes/parallel-gateways/parallel-split-config';
import { RateLimiterConfig } from '../classes/rate-limiter/rate-limiter-config';
import { RestApiReceiverConfig } from '../classes/rest/rest-api-receiver-config';
import { RestApiSenderConfig } from '../classes/rest/rest-api-sender-config';
import { S3ReaderConfig } from '../classes/s3-connectors/s3-reader-config';
import { S3WriterConfig } from '../classes/s3-connectors/s3-writer-config';
import { SalesforceGetObjectsConfig } from '../classes/salesforce/salesforce-get-objects-config';
import { SalesforceSaveObjectsConfig } from '../classes/salesforce/salesforce-save-objects-config';
import { ScheduleTaskConfig } from '../classes/schedule-tasks/schedule-task-config';
import { ScriptBlockConfig } from '../classes/script-block/script-block-config';
import { SftpReaderConfig } from '../classes/sftp-connectors/sftp-reader-config';
import { SftpWriterConfig } from '../classes/sftp-connectors/sftp-writer-config';
import { ShopifyWebhookConfig } from '../classes/shopify-webhook/shopify-webhook-config';
import { SortDataConfig } from '../classes/sort-data-task/sort-data-config';
import { TakeFromArrayConfig } from '../classes/take-from-array/take-from-array-config';
import { ValidationConfig } from '../classes/validation-task/validation-config';
import { GetVariableConfig } from '../classes/variable-services/get-variable-config';
import { SetVariableConfig } from '../classes/variable-services/set-variable-config';
import { XeroCreateInvoiceConfig } from '../classes/xero-task/xero-create-invoice-config';
import { MicrotaskType } from '../enums/microtask-types.enum';
import { MicroTaskConfigType } from '../types/micro-task-config-type';
/**
 * This function aims to properly convert the data from the database into a well structured object.
 * Since after getting the data from the DB the object will not be properly initialized as a class
 * @param taskType
 * @param config
 * @returns A proper config object class of the specified type
 */
export function InitTaskConfigClass(
	taskType: MicrotaskType,
	config?: MicroTaskConfigType
): MicroTaskConfigType {
	switch (taskType) {
		case MicrotaskType.DataMapper: {
			return new DataMapperConfig(config);
		}
		case MicrotaskType.ExecuteFlow: {
			return new ExecuteFlowConfig(config);
		}
		case MicrotaskType.IfGate: {
			return new IfGateConfig(config);
		}
		case MicrotaskType.ArrayToRecords: {
			return new ArrayToRecordsConfig(config);
		}
		case MicrotaskType.ParallelSplit: {
			return new ParallelSplitConfig(config);
		}
		case MicrotaskType.ParallelJoin: {
			return new ParallelJoinConfig(config);
		}
		case MicrotaskType.RestApiReceiver: {
			return new RestApiReceiverConfig(config);
		}
		case MicrotaskType.RestApiSender: {
			return new RestApiSenderConfig(config as RestApiSenderConfig);
		}
		case MicrotaskType.ScheduleTask: {
			return new ScheduleTaskConfig(config);
		}
		case MicrotaskType.ScriptBlock: {
			return new ScriptBlockConfig(config);
		}
		case MicrotaskType.SetVariable: {
			return new SetVariableConfig(config);
		}
		case MicrotaskType.GetVariable: {
			return new GetVariableConfig(config);
		}
		case MicrotaskType.RateLimiter: {
			return new RateLimiterConfig(config);
		}
		case MicrotaskType.ClearData: {
			return new ClearDataConfig(config);
		}
		case MicrotaskType.Delay: {
			return new DelayConfig(config);
		}
		case MicrotaskType.HubspotWebhook: {
			return new HubSpotWebhookConfig(config);
		}
		case MicrotaskType.DatabaseConnector: {
			return new DBConnectorConfig(config);
		}
		case MicrotaskType.ArrayComparison: {
			return new ArrayComparisonConfig(config as any);
		}
		case MicrotaskType.Validation: {
			return new ValidationConfig(config);
		}
		case MicrotaskType.Batching: {
			return new BatchingConfig(config);
		}
		case MicrotaskType.SFTPReader: {
			return new SftpReaderConfig(config as SftpReaderConfig);
		}
		case MicrotaskType.SFTPWriter: {
			return new SftpWriterConfig(config as SftpWriterConfig);
		}
		case MicrotaskType.S3Reader: {
			return new S3ReaderConfig(config);
		}
		case MicrotaskType.S3Writer: {
			return new S3WriterConfig(config);
		}
		case MicrotaskType.HubspotGetObjects: {
			return new HubSpotGetObjectsConfig(config as HubSpotGetObjectsConfig);
		}
		case MicrotaskType.HubspotGetAssociations: {
			return new HubSpotGetAssociationsConfig(config as HubSpotGetAssociationsConfig);
		}
		case MicrotaskType.HubspotSaveObjects: {
			return new HubSpotSaveObjectsConfig(config as HubSpotSaveObjectsConfig);
		}
		case MicrotaskType.HubspotSaveAssociations: {
			return new HubSpotSaveAssociationConfig(config as HubSpotSaveAssociationConfig);
		}
		case MicrotaskType.HubspotDeleteObjects: {
			return new HubSpotDeleteObjectsConfig(config as HubSpotDeleteObjectsConfig);
		}
		case MicrotaskType.SalesforceGetObjects: {
			return new SalesforceGetObjectsConfig(config);
		}
		case MicrotaskType.SalesforceSaveObjects: {
			return new SalesforceSaveObjectsConfig(config);
		}
		case MicrotaskType.Loop: {
			return new LoopConfig(config);
		}
		case MicrotaskType.MergeData: {
			return new MergeDataConfig(config);
		}
		case MicrotaskType.TakeFromArray: {
			return new TakeFromArrayConfig(config);
		}
		case MicrotaskType.CustomLog: {
			return new CustomLogConfig(config);
		}
		case MicrotaskType.Notification: {
			return new NotificationConfig(config);
		}
		case MicrotaskType.ShopifyWebhook: {
			return new ShopifyWebhookConfig(config);
		}
		case MicrotaskType.GoogleSheetsQuery: {
			return new GoogleSheetsQueryConfig(config);
		}
		case MicrotaskType.SortData: {
			return new SortDataConfig(config);
		}
		case MicrotaskType.DuplicateValueCheck: {
			return new DuplicateValueCheckConfig(config);
		}
		case MicrotaskType.XeroCreateInvoice: {
			return new XeroCreateInvoiceConfig(config as XeroCreateInvoiceConfig);
		}
		case MicrotaskType.AddItem: {
			return new AddItemConfig(config as AddItemConfig);
		}
		case MicrotaskType.Unknown:
		case MicrotaskType.DataSchema:
		default: {
			throw new Error('The current task type does not have a constructor.');
		}
	}
}
